// M4-04 mail and calendar for crews (program row M4-04; 02 §Personal-crew invariants: a crew is a
// subset of its owner). The owner lets their crew's agent read their mail or their calendar
// through one of the owner's OWN connections (`crews/me/connections`, the owner's session); a
// crew run reads them with its run token (`crews/run/*`), read-only, through the owner's
// connection and nothing else. Behind the `agents` add-on and two company flags: tool
// connections and `crew_mail_calendar`.
import { z } from "zod";
import {
  CrewCalendarEvent,
  CrewConnections,
  CrewMailMessage,
  CrewMailSummary,
} from "../connection-rows";
import { Timestamp, Uuid } from "../primitives";
import { CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const agents = { feature: "agents", tag: "crews" } as const;

/** The whole set: the connection each read goes through, or null for off. */
export const CrewConnectionsPut = z
  .strictObject({ mail: Uuid.nullable(), calendar: Uuid.nullable() })
  .meta({ id: "CrewConnectionsPut" });

/** RFC 3339 times; at most 62 days apart. */
export const CrewCalendarQuery = z.object({ time_min: Timestamp, time_max: Timestamp });
/** Gmail search syntax (`is:unread newer_than:1d`); 1–20 hits, 10 by default. */
export const CrewMailQuery = z.object({
  q: z.string().max(500).optional(),
  max: z.coerce.number().int().min(1).max(20).optional(),
});
export const CrewMailParams = CompanyParams.extend({
  message_id: z
    .string()
    .max(200)
    .regex(/^[A-Za-z0-9_-]{1,200}$/),
});

export const crewConnectionRoutes = {
  "crews.connections": defineRoute({
    ...agents,
    id: "crews.connections",
    method: "get",
    path: `${base}/crews/me/connections`,
    summary: "Whether my agent may read my mail and my calendar, and through which connection",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: CrewConnections },
    errors: [],
    emits: [],
  }),
  "crews.putConnections": defineRoute({
    ...agents,
    id: "crews.putConnections",
    method: "put",
    path: `${base}/crews/me/connections`,
    summary: "Let my agent read my mail or calendar through one of my own connections (or not)",
    access: "member",
    params: CompanyParams,
    body: CrewConnectionsPut,
    idempotency: "optional",
    response: { status: 200, schema: CrewConnections },
    errors: ["grant_not_allowed", "connection_path_disabled"],
    emits: ["crew_connection_grant.changed"],
  }),
  "crewRun.calendar": defineRoute({
    ...agents,
    id: "crewRun.calendar",
    method: "get",
    path: `${base}/crews/run/calendar`,
    summary: "A crew run reads its owner's calendar events (read-only, the owner's connection)",
    access: "run_token",
    params: CompanyParams,
    query: CrewCalendarQuery,
    response: { status: 200, schema: z.object({ data: z.array(CrewCalendarEvent) }) },
    errors: [
      "crew_connection_off",
      "connection_unusable",
      "vendor_unavailable",
      "content_quarantined",
    ],
    // AGENT-SAFETY: the content screen's `note` step is `read:content_screen`'s (events.ts)
    emits: [],
  }),
  "crewRun.mail": defineRoute({
    ...agents,
    id: "crewRun.mail",
    method: "get",
    path: `${base}/crews/run/mail`,
    summary: "A crew run searches its owner's mail: senders, subjects, snippets (read-only)",
    access: "run_token",
    params: CompanyParams,
    query: CrewMailQuery,
    response: { status: 200, schema: z.object({ data: z.array(CrewMailSummary) }) },
    errors: [
      "crew_connection_off",
      "connection_unusable",
      "vendor_unavailable",
      "content_quarantined",
    ],
    // AGENT-SAFETY: the content screen's `note` step is `read:content_screen`'s (events.ts)
    emits: [],
  }),
  "crewRun.mailMessage": defineRoute({
    ...agents,
    id: "crewRun.mailMessage",
    method: "get",
    path: `${base}/crews/run/mail/{message_id}`,
    summary: "A crew run reads one of its owner's messages (plain text; attachments by name)",
    access: "run_token",
    params: CrewMailParams,
    response: { status: 200, schema: CrewMailMessage },
    errors: [
      "crew_connection_off",
      "connection_unusable",
      "vendor_unavailable",
      "content_quarantined",
    ],
    // AGENT-SAFETY: the content screen's `note` step is `read:content_screen`'s (events.ts)
    emits: [],
  }),
} as const;
